import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import type { QuickViewId } from "@/lib/messengerStoryLabels";
import MessengerUserProfile from "@/components/MessengerUserProfile";
import MessengerActiveFilters, {
  buildActiveFilterChips,
} from "@/components/messenger/MessengerActiveFilters";
import MessengerFilterSidebar, {
  buildBSignalOptions,
  buildClassOptions,
  buildPersonaOptions,
  buildThemeOptions,
} from "@/components/messenger/MessengerFilterSidebar";
import MessengerPageShell from "@/components/messenger/MessengerPageShell";
import MessengerQuickViews from "@/components/messenger/MessengerQuickViews";
import MessengerStatsStrip from "@/components/messenger/MessengerStatsStrip";
import MessengerStoryList from "@/components/messenger/MessengerStoryList";
import VoiceResearchChat from "@/components/voice/VoiceResearchChat";

function useDebounced<T>(value: T, delayMs: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delayMs);
    return () => clearTimeout(t);
  }, [value, delayMs]);
  return debounced;
}

function deriveQuickViewId(
  storyClass?: string,
  persona?: string,
  hasPurchase?: boolean,
  q?: string,
  month?: string,
  tier?: "story" | "voice",
  theme?: string,
  bSignal?: string,
): QuickViewId {
  if (q?.trim() || month || theme || bSignal) return "all";
  if (hasPurchase) return "kupili";
  if (persona === "P6") return "opiekunka";
  if (storyClass === "produkt_zakup") return "produkt";
  if (storyClass === "pytanie") return "pytanie";
  if (storyClass === "wsparcie") return "wsparcie";
  if (tier === "voice") return "voice";
  if (tier === "story") return "historia";
  if (storyClass === "historia_identyfikacja") return "historia";
  if (!storyClass && !persona && !hasPurchase && !tier) return "all";
  return "all";
}

export default function HistorieMessenger() {
  const qc = useQueryClient();
  const [q, setQ] = useState("");
  const [storyClass, setStoryClass] = useState<string | undefined>();
  const [persona, setPersona] = useState<string | undefined>();
  const [theme, setTheme] = useState<string | undefined>();
  const [bSignal, setBSignal] = useState<string | undefined>();
  const [month, setMonth] = useState<string | undefined>();
  const [hasPurchase, setHasPurchase] = useState<boolean | undefined>();
  const [tier, setTier] = useState<"story" | "voice" | undefined>();
  const [profileParticipantId, setProfileParticipantId] = useState<string | null>(null);
  const [searchMode, setSearchMode] = useState<"keyword" | "semantic">("keyword");

  const debouncedQ = useDebounced(q, 300);

  const { data: facetsData } = useQuery({
    queryKey: ["messenger-stories-facets"],
    queryFn: api.messengerStoriesFacets,
  });

  const { data: statsData } = useQuery({
    queryKey: ["messenger-stories-stats"],
    queryFn: api.messengerStoriesStats,
  });

  const { data, isLoading, isFetching } = useQuery({
    queryKey: [
      "messenger-stories",
      debouncedQ,
      storyClass,
      persona,
      theme,
      bSignal,
      month,
      hasPurchase,
      tier,
      searchMode,
    ],
    queryFn: () =>
      api.messengerStories({
        q: debouncedQ || undefined,
        class: storyClass,
        persona,
        theme,
        b_signal: bSignal,
        month,
        has_purchase: hasPurchase,
        tier,
        mode: searchMode,
        limit: 100,
      }),
  });

  const reindex = useMutation({
    mutationFn: api.messengerStoriesReindexSemantic,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["messenger-stories"] });
      qc.invalidateQueries({ queryKey: ["messenger-stories-facets"] });
      qc.invalidateQueries({ queryKey: ["messenger-stories-stats"] });
    },
  });

  const facets = facetsData?.payload;
  const stats = statsData?.payload;
  const rows = data?.items ?? [];
  const indexExists = data?.index_exists ?? facets?.index_exists ?? false;
  const totalAll = facets?.total ?? 0;

  const classMap = facets?.classes ?? {};
  const personaMap = facets?.personas ?? {};
  const monthMap = facets?.months ?? {};
  const themeMap = facets?.themes ?? {};
  const bSignalMap = facets?.b_signals ?? {};
  const tierMap = facets?.tiers ?? {};

  const classOptions = buildClassOptions(classMap);
  const personaOptions = buildPersonaOptions(personaMap);
  const themeOptions = buildThemeOptions(themeMap);
  const bSignalOptions = buildBSignalOptions(bSignalMap);
  const monthOptions = Object.entries(monthMap).sort((a, b) => b[0].localeCompare(a[0]));

  const activeQuickView = deriveQuickViewId(
    storyClass,
    persona,
    hasPurchase,
    debouncedQ,
    month,
    tier,
    theme,
    bSignal,
  );

  const quickViewCounts: Partial<Record<QuickViewId, number>> = {
    all: totalAll,
    historia: tierMap.story ?? classMap.historia_identyfikacja,
    voice: tierMap.voice,
    pytanie: classMap.pytanie,
    wsparcie: classMap.wsparcie,
    produkt: classMap.produkt_zakup,
    opiekunka: personaMap.P6,
  };

  const clearAllFilters = () => {
    setQ("");
    setStoryClass(undefined);
    setPersona(undefined);
    setTheme(undefined);
    setBSignal(undefined);
    setMonth(undefined);
    setHasPurchase(undefined);
    setTier(undefined);
  };

  const applyQuickView = (id: QuickViewId) => {
    clearAllFilters();
    if (id === "historia") setTier("story");
    if (id === "voice") setTier("voice");
    if (id === "pytanie") setStoryClass("pytanie");
    if (id === "wsparcie") setStoryClass("wsparcie");
    if (id === "produkt") setStoryClass("produkt_zakup");
    if (id === "opiekunka") setPersona("P6");
    if (id === "kupili") setHasPurchase(true);
  };

  const activeChips = buildActiveFilterChips(
    { q, storyClass, persona, theme, bSignal, month, hasPurchase, tier },
    {
      clearQ: () => setQ(""),
      clearClass: () => setStoryClass(undefined),
      clearPersona: () => setPersona(undefined),
      clearTheme: () => setTheme(undefined),
      clearBSignal: () => setBSignal(undefined),
      clearMonth: () => setMonth(undefined),
      clearPurchase: () => setHasPurchase(undefined),
      clearTier: () => setTier(undefined),
    },
  );

  const searchHint = (() => {
    if (searchMode !== "semantic") return null;
    if (data?.search_fallback === "keyword") {
      return "Indeks niedostępny: użyto wyszukiwania po słowach.";
    }
    const at = data?.indexed_at;
    const n = data?.indexed_count ?? 0;
    if (!at) return "Brak indeksu. Kliknij Przeindeksuj kontekst.";
    return `Indeks: ${String(at).slice(0, 10)} · ${n} wektorów`;
  })();

  return (
    <MessengerPageShell
      total={data?.total ?? totalAll}
      q={q}
      onQChange={setQ}
      searchMode={searchMode}
      onSearchModeChange={setSearchMode}
      searchHint={searchHint}
      searchPending={isFetching}
      indexedCount={data?.indexed_count ?? totalAll}
      onReindex={() => reindex.mutate()}
      reindexPending={reindex.isPending}
      aside={<VoiceResearchChat surface="messenger_chat" />}
    >
      <MessengerStatsStrip stats={stats} />

      <MessengerQuickViews
        activeId={activeQuickView}
        counts={quickViewCounts}
        onSelect={applyQuickView}
      />

      <div className="flex flex-col gap-6 lg:flex-row lg:items-start">
        <MessengerFilterSidebar
          classOptions={classOptions}
          personaOptions={personaOptions}
          themeOptions={themeOptions}
          bSignalOptions={bSignalOptions}
          monthOptions={monthOptions}
          storyClass={storyClass}
          persona={persona}
          theme={theme}
          bSignal={bSignal}
          month={month}
          hasPurchase={hasPurchase}
          totalAll={totalAll}
          onClassChange={setStoryClass}
          onPersonaChange={setPersona}
          onThemeChange={setTheme}
          onBSignalChange={setBSignal}
          onMonthChange={setMonth}
          onPurchaseChange={setHasPurchase}
        />

        <div className="min-w-0 flex-1">
          <MessengerActiveFilters chips={activeChips} onClearAll={clearAllFilters} />
          <MessengerStoryList
            stories={rows}
            isLoading={isLoading}
            indexExists={indexExists}
            onOpenProfile={setProfileParticipantId}
          />
        </div>
      </div>

      <MessengerUserProfile
        participantId={profileParticipantId}
        onClose={() => setProfileParticipantId(null)}
      />
    </MessengerPageShell>
  );
}
